/**
 * Switch — Radix-based toggle switch primitive.
 *
 * Tablet-first invariants (UX Blueprint §9):
 *   - Total target area >= 44px (Radix root is 44x24 + surrounding label padding)
 *   - Color + position: state is communicated by both background color AND thumb X position
 *   - Visible focus ring on keyboard nav
 *   - Respects `prefers-reduced-motion` (transition is short)
 */
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { cn } from '../utils/cn';

export const Switch = forwardRef<
  ElementRef<typeof SwitchPrimitive.Root>,
  ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(({ className, ...rest }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn(
      'peer inline-flex h-[28px] w-[48px] shrink-0 cursor-pointer items-center rounded-biz-full',
      'border-2 border-transparent',
      'transition-colors duration-biz-fast',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring focus-visible:ring-offset-2',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'data-[state=checked]:bg-biz-primary data-[state=unchecked]:bg-biz-muted-foreground/40',
      className,
    )}
    {...rest}
  >
    <SwitchPrimitive.Thumb
      className={cn(
        'pointer-events-none block h-5 w-5 rounded-biz-full bg-biz-primary-foreground shadow-biz-sm',
        'ring-0 transition-transform duration-biz-fast',
        'data-[state=checked]:translate-x-[22px] data-[state=unchecked]:translate-x-0',
      )}
    />
  </SwitchPrimitive.Root>
));
Switch.displayName = 'Switch';
